import { createReducer, on } from "@ngrx/store";
import {
	getFragment,
	getPathName,
	getQueryParams,
	replaceFragment,
	replaceQueryParams,
	updateState,
} from "@obg/ngx.global";
import { isEqual } from "lodash-es";

import { RouteAction } from "./route.action";
import { Route, RouteState } from "./route.model";

const INITIAL_STATE: RouteState = {
	isNavigating: false,
	hasHistory: false,
	shouldActivate: undefined,
};

export const routeReducer = createReducer(
	INITIAL_STATE,
	on(RouteAction.navigationStart, (state) =>
		updateState(state, {
			isNavigating: true,
		})
	),
	on(RouteAction.resolveEnd, (state, { payload: { current, hasHistory } }) => {
		const hasRouteChanged =
			!state.current ||
			state.current.name !== current.name ||
			state.current.fragment !== current.fragment ||
			!isEqual(state.current.params, current.params) ||
			!isEqual(state.current.queryParams, current.queryParams);

		return updateState(state, {
			referer: hasRouteChanged && !current.extras?.state?.keepPreviousReferer ? state.current : state.referer,
			current,
			hasHistory,
		});
	}),
	on(RouteAction.guardsCheckEnd, (state, { payload: { shouldActivate } }) =>
		updateState(state, {
			shouldActivate,
		})
	),
	on(RouteAction.navigationEnd, RouteAction.navigationCancel, RouteAction.navigationError, (state) =>
		updateState(state, {
			isNavigating: false,
			shouldActivate: undefined,
		})
	),
	on(RouteAction.replacePath, (state, { externalUrl }) => {
		if (state.current?.externalUrl === externalUrl) {
			return state;
		}
		return updateCurrentRoute(state, {
			externalUrl,
			pathName: getPathName(externalUrl),
			fragment: getFragment(externalUrl),
			queryParams: getQueryParams(externalUrl),
		});
	}),
	on(RouteAction.setFragment, (state, { fragment, replaceUrl }) => {
		const hasHistory = !replaceUrl || state.hasHistory;
		if (state.current && fragment === state.current.fragment) {
			return state;
		}
		return updateCurrentRoute(
			{ ...state, hasHistory },
			{
				fragment,
				isFragmentSecure: undefined,
				externalUrl: replaceFragment(state.current?.externalUrl, fragment),
			}
		);
	}),
	on(RouteAction.setQueryParamsSuccess, (state, { queryParams }) => {
		if (!state.current) {
			return state;
		}
		if (isEqual(queryParams, state.current.queryParams)) {
			return state;
		}
		return updateCurrentRoute(state, {
			queryParams,
			externalUrl: replaceQueryParams(state.current.externalUrl, queryParams),
		});
	}),
	on(RouteAction.setSecureFragmentStatus, (state, { payload }) =>
		updateCurrentRoute(state, {
			isFragmentSecure: payload,
		})
	),
	on(RouteAction.setAdditionalData, (state, { payload }) =>
		updateState(state, {
			additionalData: payload,
		})
	),
	on(RouteAction.setDataForSubArea, (state, { payload }) =>
		updateState(state, {
			subArea: payload.subArea,
		})
	),
	on(RouteAction.setAdditionalDataAndNavigate, (state, { additionalData }) =>
		updateState(state, {
			additionalData,
		})
	)
);

function updateCurrentRoute(state: RouteState, updates: Partial<Route>): RouteState {
	if (!state.current) {
		return state;
	}

	return updateState(state, {
		current: {
			...state.current,
			...updates,
		},
	});
}
